import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { AlertUnsavedData, Modal, ModalSize, NotificationToaster } from "components/notifications";
import { FormTextInput, FormSwitch, FormNumericInput } from "components/form-fields";
import { Button, ButtonSave } from "components/buttons";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PcnService } from "services/PcnService";
import { Intent, Alignment } from "@blueprintjs/core";

const statusNameMaxLength = 50;
const badRequestCode = 400;
const defaultModel = { id: 0, statusName: "", openStatus: false, reviewDays: "" };

export function PcnStatusModal({ showModal, modalTitle, isUpdate = false, loading = false, onSave, onCloseModal, statusDetails = defaultModel }) {
    const [original, setOriginal] = useState(defaultModel);
    const [current, setCurrent] = useState(defaultModel);
    const [saving, setSaving] = useState(false);
    const [blockModalClose, setBlockModalClose] = useState(false);
    const isDirty = useDirtyData(original, current);
    const [isValid, errors, validate] = useValidation();

    useEffect(loadDetails, [statusDetails, showModal]);

    function loadDetails() {
        if (!showModal) {
            return;
        }
        // Use the nullish coalescing operator for openStatus and reviewDays so a
        // legitimate "false" or "0" value from the API is preserved rather than
        // being replaced by the default (which "||" would incorrectly do).
        const mapped = {
            id: statusDetails.id || defaultModel.id,
            statusName: statusDetails.statusName || defaultModel.statusName,
            openStatus: statusDetails.openStatus ?? defaultModel.openStatus,
            reviewDays: statusDetails.reviewDays ?? defaultModel.reviewDays
        };
        setOriginal(mapped);
        setCurrent(mapped);
    }

    function onFieldChange(fieldName, passedValue) {
        setCurrent({ ...current, [fieldName]: passedValue });
    }

    useEffect(runValidation, [current, showModal, loading]);

    function runValidation() {
        if (!showModal || loading) {
            return;
        }
        const rules = [
            { fieldName: "statusName", required: true, maxLength: statusNameMaxLength },
            { fieldName: "reviewDays", required: true, type: "number", minValue: 0 }
        ];
        validate(rules, current);
    }

    function onSaveClick() {
        if (!isValid || !isDirty || saving || loading) {
            return;
        }
        const request = { statusName: current.statusName, openStatus: current.openStatus, reviewDays: current.reviewDays };
        setSaving(true);
        const savePromise = isUpdate ? PcnService.updateStatus(current.id, request) : PcnService.createStatus(request);
        savePromise
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, isUpdate ? "Status updated successfully." : "Status created successfully.");
                onSaveComplete();
            })
            .catch((error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not save the status. Please try again.");
                }
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onSaveComplete() {
        resetModal();
        onSave();
    }

    function onCloseClick(forceClose = false) {
        if (isDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }
        resetModal();
        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginal(defaultModel);
        setCurrent(defaultModel);
    }

    return (
        <Fragment>
            <Modal isOpen={showModal} onClose={() => onCloseClick(false)} clickOutsideClose={true} title={modalTitle} size={ModalSize.MEDIUM} updateStateOnClose={false}
                footer={(
                    <div className="bp6-dialog-footer-actions">
                        <Button text="Cancel" intent={Intent.SECONDARY} onClick={() => onCloseClick(true)} />
                        <ButtonSave id="save-status-button" onClick={onSaveClick} simpleDisabled={!isValid || loading || !isDirty} disabled={saving} text={"Save"} />
                    </div>
                )}>
                <FormTextInput id="input-status-name" disabled={saving} loading={loading} value={current.statusName} headingText="Status Name" placeholder="Please type the status name here" onChange={(event) => onFieldChange("statusName", event.target.value)} dangerHelperText={errors.statusName} maxLength={statusNameMaxLength} large={true} fill={true} />
                <FormSwitch id="input-status-open" disabled={saving} loading={loading} checked={current.openStatus} labelElement="Open:" inline={true} alignment={Alignment.END} onChange={(event) => onFieldChange("openStatus", event.target.checked)} />
                <FormNumericInput id="input-status-review-days" disabled={saving} loading={loading} selectedValue={current.reviewDays} headingText="Review Days" placeholder="Enter the number of review days" min={0} max={365} onValueChange={(value) => onFieldChange("reviewDays", value)} dangerHelperText={errors.reviewDays} fill={true} />
            </Modal>
            <AlertUnsavedData isDirty={isDirty} isDirtySamePage={blockModalClose} onCancelLeave={() => setBlockModalClose(false)} onConfirmLeave={() => onCloseClick(true)} />
        </Fragment>
    );
}

PcnStatusModal.propTypes = { showModal: PropTypes.bool.isRequired, modalTitle: PropTypes.string.isRequired, isUpdate: PropTypes.bool, loading: PropTypes.bool, onSave: PropTypes.func.isRequired, onCloseModal: PropTypes.func.isRequired, statusDetails: PropTypes.object };
